import React, { useCallback } from "react";
import { ConfirmDialog, ConfirmDialogProps } from "./ConfirmDialog";
import { Box, CircularProgress, DialogTitle, Typography } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { useUILocale } from "../UILocale";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
dayjs.extend(duration);

type SessionTimeoutDialogProps = Pick<ConfirmDialogProps, "open"> & {
  expiration: Date | null;
  handleSessionExtend: () => void;
  handleLogout: () => void;
};

export function SessionTimeoutDialog({
  handleLogout,
  handleSessionExtend,
  expiration,
  ...dialogProps
}: SessionTimeoutDialogProps) {
  const {
    dialog: {
      sessionTimeout: { headline, body, confirmButton, dismissButton },
    },
  } = useUILocale();
  const onConfirm = useCallback(() => {
    handleSessionExtend();
  }, [handleSessionExtend]);

  const onDismiss = useCallback(() => {
    handleLogout();
  }, [handleLogout]);

  return (
    <ConfirmDialog
      id="timeout-dialog"
      aria-label={headline}
      confirmButtonText={confirmButton}
      dismissButtonText={dismissButton}
      onConfirm={onConfirm}
      onDismiss={onDismiss}
      {...dialogProps}
    >
      <DialogTitle>{headline}</DialogTitle>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: 2,
        }}
      >
        <SessionTimeoutCountdown
          warningPeriodMs={expiration ? expiration.getTime() - Date.now() : 0}
        />
        <Typography>{body}</Typography>
      </Box>
    </ConfirmDialog>
  );
}

const normalizeToSecond = (ms: number) =>
  Math.max(0, Math.ceil(ms / 1000) * 1000);

interface SessionTimoutCountdownProps {
  warningPeriodMs: number;
}

// I couldn't find a good way to do this with DayJS duration formatting so I wrote this.
function formatTimeRemainingForAnnouncement(ms: number) {
  const totalSeconds = Math.ceil(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const locale = new Intl.NumberFormat(
    document.documentElement.lang,
  ).resolvedOptions().locale;
  const parts = [];
  if (minutes > 0) {
    parts.push(
      new Intl.NumberFormat(locale, {
        style: "unit",
        unit: "minute",
        unitDisplay: "long",
      }).format(minutes),
    );
  }
  if (seconds > 0) {
    parts.push(
      new Intl.NumberFormat(locale, {
        style: "unit",
        unit: "second",
        unitDisplay: "long",
      }).format(seconds),
    );
  }
  return new Intl.ListFormat(locale, {
    style: "long",
    type: "conjunction",
  }).format(parts);
}

export default function SessionTimeoutCountdown({
  warningPeriodMs,
}: SessionTimoutCountdownProps) {
  // set initial
  const [progress, setProgress] = React.useState(100);
  const [timeRemaining, setTimeRemaining] = React.useState(
    normalizeToSecond(warningPeriodMs),
  );
  const [announcement, setAnnouncement] = React.useState("");

  React.useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining((prevTime) => {
        const newTimeRemaining = prevTime <= 0 ? 0 : prevTime - 1000;
        if (newTimeRemaining === 0) {
          clearInterval(timer);
          setProgress(0);
          setAnnouncement(formatTimeRemainingForAnnouncement(0));
          return newTimeRemaining;
        }
        setProgress((newTimeRemaining / warningPeriodMs) * 100);
        const announcementInterval = 30 * 1000; // announce every 30 seconds
        if (newTimeRemaining % announcementInterval === 0) {
          setAnnouncement(formatTimeRemainingForAnnouncement(newTimeRemaining));
        }
        return newTimeRemaining;
      });
    }, 1000);
    return () => {
      clearInterval(timer);
    };
  }, [warningPeriodMs]);

  return (
    <>
      <Box
        role="status"
        aria-live="polite"
        aria-atomic="true"
        sx={visuallyHidden}
      >
        {announcement}
      </Box>
      <Box sx={{ position: "relative", display: "inline-flex" }}>
        <CircularProgress
          variant="determinate"
          aria-hidden="true"
          value={progress}
        />
        <Box
          sx={{
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            position: "absolute",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box role="timer" aria-atomic="true">
            <Typography
              variant="caption"
              component="div"
              sx={{ color: "text.secondary" }}
            >
              {dayjs.duration(timeRemaining).format("m:ss")}
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
}
